import React from 'react';
import './index.css';
import { createRoot } from 'react-dom/client';
(function installPolyfills() {
  // structuredClone polyfill (Chrome <98, Firefox <94, Safari <15.4)
  if (typeof (globalThis as any).structuredClone !== 'function') {
    ;
    (globalThis as any).structuredClone = function structuredClonePolyfill<T>(value: T): T {
      // JSON round-trip — safe for plain data (no Date/Map/Set/undefined,
      // but our call sites only clone JSON-compatible state).
      try {
        return JSON.parse(JSON.stringify(value)) as T;
      } catch {
        // Last-resort identity return — better than crashing the module.
        return value;
      }
    };
  }
})();
// Detect browsers too old to run the app even with the polyfill above.
// We probe for APIs introduced around the same era as structuredClone.
function detectUnsupportedBrowser(): string | null {
  if (typeof globalThis === 'undefined') return 'globalThis';
  if (typeof Promise === 'undefined') return 'Promise';
  if (typeof Map === 'undefined') return 'Map';
  if (typeof Set === 'undefined') return 'Set';
  if (typeof Symbol === 'undefined') return 'Symbol';
  if (typeof WeakMap === 'undefined') return 'WeakMap';
  if (typeof fetch === 'undefined') return 'fetch';
  if (typeof Object.fromEntries !== 'function') return 'Object.fromEntries';
  if (typeof Array.prototype.flat !== 'function') return 'Array.prototype.flat';
  return null;
}
// Detect whether a thrown error looks like an "old browser" runtime failure
// (ReferenceError on a known modern global) rather than an app bug.
function looksLikeUnsupportedBrowserError(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const msg = err.message || '';
  // Common shapes: "X is not defined", "Can't find variable: X"
  const knownModernGlobals = ['structuredClone', 'globalThis', 'queueMicrotask', 'BigInt', 'ResizeObserver', 'IntersectionObserver'];
  for (const g of knownModernGlobals) {
    if (msg.includes(g) && /not defined|can't find variable/i.test(msg)) {
      return g;
    }
  }
  return null;
}
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element. Make sure your HTML file has a <div id="root"></div> element.');
}
const root = createRoot(rootElement);
// ──────────────────────────────────────────────────────────────────────────
// Boot-time crash visibility
//
// The app was silently hanging on a blank screen because a module-evaluation
// or first-render error left no React tree at all. The static index.html had
// nothing to show, so the user saw an empty page with no indication of what
// went wrong.
//
// We now:
//  1. Wrap the App import + render in try/catch so synchronous module load /
//     render errors are surfaced visually.
//  2. Hook window.onerror + unhandledrejection so async failures also surface.
//  3. Render a clear error panel with the actual error message and stack so
//     the next iteration can fix the real root cause instead of guessing.
//  4. NEW: detect old-browser errors and route to a Djay-styled
//     "deze browser wordt niet ondersteund" screen instead of the technical
//     panel.
// ──────────────────────────────────────────────────────────────────────────
async function renderUnsupportedBrowser(missingFeature?: string) {
  try {
    const mod = await import('./src/components/shared/UnsupportedBrowserScreen');
    const UnsupportedBrowserScreen = mod.UnsupportedBrowserScreen;
    root.render(<UnsupportedBrowserScreen missingFeature={missingFeature} data-id="element-0" data-mp="3ac1fqx26i1e4" />);
  } catch {
    // If even the unsupported-browser screen can't load, fall back to inline HTML.
    rootElement!.innerHTML = `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,sans-serif;background:linear-gradient(135deg,#fff7ed,#fef3c7)">
      <div style="max-width:480px;background:white;border:2px solid #fed7aa;border-radius:24px;padding:32px;text-align:center;box-shadow:0 10px 40px rgba(0,0,0,0.08)">
        <div style="font-size:48px;margin-bottom:12px">🦝</div>
        <h1 style="font-size:22px;font-weight:800;color:#1f2937;margin:0 0 8px">Hoi! Je browser is een beetje oud</h1>
        <p style="color:#475569;margin:0 0 16px;line-height:1.5">Meester Djay werkt het beste in een moderne browser. Probeer Chrome, Firefox, Safari of Edge (versie vanaf 2022).</p>
        <button onclick="window.location.reload()" style="padding:12px 24px;background:#ea580c;color:white;border:none;border-radius:12px;font-weight:600;font-size:15px;cursor:pointer">Probeer opnieuw</button>
        ${missingFeature ? `<p style="margin-top:16px;font-size:11px;font-family:monospace;color:#94a3b8">Ontbrekende functie: ${missingFeature}</p>` : ''}
      </div>
    </div>`;
  }
}
function renderBootError(label: string, err: unknown) {
  // First: if this looks like an old-browser failure, show the friendly screen.
  const missingGlobal = looksLikeUnsupportedBrowserError(err);
  if (missingGlobal) {
    void renderUnsupportedBrowser(missingGlobal);
    return;
  }
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error && err.stack ? err.stack : '';
  try {
    root.render(<div role="alert" style={{
      minHeight: '100vh',
      background: '#FFF9F2',
      color: '#1f2937',
      padding: '24px',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center'
    }} data-id="element-1" data-mp="2u7aexjb6huqa">
        <div style={{
        maxWidth: 720,
        width: '100%'
      }} data-id="element-2" data-mp="2bgszuhj9qxne">
          <div style={{
          fontSize: 28,
          marginBottom: 8
        }} data-id="element-3" data-mp="26hg231qv0661">
            ⚠️
          </div>
          <h1 style={{
          fontSize: 20,
          fontWeight: 700,
          margin: 0,
          marginBottom: 4
        }} data-id="element-4" data-mp="21k6ezsqy06ld">
            Meester Djay kon niet starten
          </h1>
          <p style={{
          fontSize: 14,
          color: '#475569',
          marginTop: 0
        }} data-id="element-5" data-mp="1hwth2k64uaaq">
            {label}
          </p>
          <pre style={{
          marginTop: 16,
          padding: 12,
          background: '#fff',
          border: '1px solid #fde68a',
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'auto'
        }} data-id="element-6" data-mp="2akz114pd3v9i">
            <strong style={{
            color: '#b45309'
          }} data-id="element-7" data-mp="2tjthci4h53rs">
              {message}
            </strong>
            {stack ? `\n\n${stack}` : ''}
          </pre>
          <button onClick={() => window.location.reload()} style={{
          marginTop: 16,
          padding: '8px 14px',
          background: '#ea580c',
          color: 'white',
          borderRadius: 8,
          border: 'none',
          fontWeight: 600,
          cursor: 'pointer'
        }} data-id="element-8" data-mp="mvb0ha6wx0qj">
            Pagina vernieuwen
          </button>
        </div>
      </div>);
  } catch {
    // Last-resort fallback: write directly to the DOM so we never end up
    // staring at a blank screen again.
    rootElement!.innerHTML = `<div style="padding:24px;font-family:system-ui">
      <h1 style="font-size:20px">Meester Djay kon niet starten</h1>
      <p>${label}</p>
      <pre style="white-space:pre-wrap;background:#fff;padding:12px;border-radius:8px;border:1px solid #fde68a">${message}\n\n${stack}</pre>
    </div>`;
  }
}
// Surface async / unhandled errors before any React render has happened.
window.addEventListener('error', e => {
  // Only intercept if the root is still empty (= we never managed to render).
  if (rootElement && rootElement.childElementCount === 0) {
    renderBootError('Onverwachte fout tijdens opstarten', e.error ?? e.message);
  }
});
window.addEventListener('unhandledrejection', e => {
  if (rootElement && rootElement.childElementCount === 0) {
    renderBootError('Onverwachte fout tijdens opstarten (async)', e.reason ?? 'Promise rejected');
  }
});
// Pre-flight: if essential modern APIs are missing, skip the app load entirely
// and go straight to the friendly screen.
const missingFeature = detectUnsupportedBrowser();
if (missingFeature) {
  void renderUnsupportedBrowser(missingFeature);
} else {
  ;
  (async () => {
    try {
      const mod = await import('./src/App');
      const App = mod.App;
      root.render(<App data-id="element-9" data-mp="2c5wjk5n5ecks" />);
    } catch (err) {
      renderBootError('Een module kon niet geladen worden tijdens opstarten', err);
    }
  })();
}